// Orchestrator des PIPR-Charts.
//
// React besitzt den Container und den Zustand (Bank, Zoomstufe, Ansicht,
// Hover), D3 besitzt ausschließlich die Kinder des SVG-Refs. Das Modell wird bei
// jeder Änderung von Daten, Breite, Zoomstufe oder Ansicht neu gebaut; der Hover
// steht bewusst nicht in den Abhängigkeiten, damit beim Fahren über den Chart
// nur die Leseleiste neu gerendert wird.
//
// Ein Bankwechsel verwirft Hover und Zoomstufe: die beiden Historien fangen zu
// verschiedenen Zeitpunkten an, ein gehaltener Ausschnitt wäre nach dem Wechsel
// nicht derselbe Ausschnitt.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";

import { ChartHeader } from "./ChartHeader";
import { buildDays, buildFwdModal, buildMeetingDates, buildModel } from "./chart/model";
import type { Hover, ModeKey, RangeKey } from "./chart/model";
import { chartHeight } from "./chart/layout";
import { renderChart } from "./chart/render";
import { usePiprData } from "./data/usePiprData";
import { localDateTime, localZone, tsDate } from "./format";
import { PALETTE } from "./palette";
import { buildSummary } from "./summary";
import { stringsFor } from "./strings";
import type { Bank } from "./types";

interface PiprChartProps {
    language: string;
    initialBank?: Bank;
}

const NBSP = "\u00a0";

const CARD_MIN_H = {
    minHeight: 945,
    "@media (min-width:700px)": { minHeight: 837 },
};

export const PiprChart = ({ language, initialBank = "fed" }: PiprChartProps) => {
    const lang = language === "en" ? "en" : "de";
    const s = stringsFor(lang);

    const [bank, setBank] = useState<Bank>(initialBank);
    const { data, loading, error } = usePiprData(bank);

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    const [width, setWidth] = useState(0);
    const [range, setRange] = useState<RangeKey>("y1");
    const [mode, setMode] = useState<ModeKey>("rate");
    const [hover, setHover] = useState<Hover>(null);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        setWidth(el.offsetWidth);
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) setWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const days = useMemo(() => (data ? buildDays(data) : []), [data]);
    const meetingDates = useMemo(() => (data ? buildMeetingDates(data) : []), [data]);
    const fwdModal = useMemo(() => (data ? buildFwdModal(data) : []), [data]);

    const model = useMemo(() => {
        if (!data) return null;
        return buildModel({ data, days, meetingDates, fwdModal, width, range, mode });
    }, [data, days, meetingDates, fwdModal, width, range, mode]);

    const handleHover = useCallback((h: Hover) => setHover(h), []);

    const handleBank = useCallback((b: Bank) => {
        setBank(b);
        setHover(null);
        setRange("y1");
    }, []);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        if (!model) {
            while (svg.firstChild) svg.removeChild(svg.firstChild);
            return;
        }
        renderChart(svg, model, s, lang, handleHover);
    }, [model, s, lang, handleHover]);

    const summary = useMemo(
        () => (data ? buildSummary(data, days, hover, s, lang) : null),
        [data, days, hover, s, lang],
    );

    const height = chartHeight(width);
    const pendingOpen = Boolean(data && data.forward.pendingBp !== 0);

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: PALETTE.card,
                borderRadius: "10px",
                p: { xs: 1.75, md: 3 },
                width: "100%",
                ...CARD_MIN_H,
                textAlign: "left",
            }}
        >
            <ChartHeader
                strings={s}
                summary={summary}
                bank={bank}
                pendingOpen={pendingOpen}
                range={range}
                mode={mode}
                onBank={handleBank}
                onRange={setRange}
                onMode={setMode}
            />

            <Box ref={containerRef} sx={{ width: "100%", height, position: "relative" }}>
                <svg
                    ref={svgRef}
                    width={width}
                    height={height}
                    role="img"
                    aria-label={s.aria}
                    style={{ background: "transparent", display: "block" }}
                />

                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <CircularProgress size={22} sx={{ color: PALETTE.inkMuted }} />
                    </Box>
                )}

                {error && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            px: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: PALETTE.inkMuted, textAlign: "center" }}
                        >
                            {error === "schema" ? s.errSchema : s.errNetwork}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Typography
                variant="caption"
                sx={{ color: PALETTE.inkFaint, display: "block", mt: 1, minHeight: 18 }}
            >
                {data
                    ? `${s.asOf} ${localDateTime(tsDate(data.generatedTs), lang)}  ·  ${s.times} ${localZone()}`
                    : NBSP}
            </Typography>
        </Paper>
    );
};